<script setup lang="ts">
/**
 * WorkContextBar · 工作页签条（46px，--workbar-height，系统骨架）
 * ------------------------------------------------------------
 * 导航四按钮（AI 助手 / 主页 / 关闭其他标签 / 返回）+ 工作页签列（打开即成签：
 * 点击切换、×关闭、超限自动淘汰、窄屏横滚）。任何视图都经由
 * router.afterEach 进入页签栏。快捷键保留：Esc 返回 · Alt+← 后退 · Alt+H 默认主页。
 */
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWorkTabs, closeTab, closeOthers } from '@/domain/worktabs'
import { useAiBuddy } from './aibuddy'
import { useToast } from '@/design/ui/UToast'
import UDrawer from '@/design/ui/UDrawer.vue'
import { defaultHome } from './router'
import { on, off } from '@/shared/events'
import { ENABLED_MODULES } from './registry'
import {
  versionPublicRepo,
  type VersionLedgerItem,
  type VersionRelease,
} from '@/data/api/sys-admin-repository'

const route = useRoute()
const router = useRouter()
const wt = useWorkTabs()
const buddy = useAiBuddy()
const toast = useToast().show

/* 返回：本会话导航轨迹（含子页面跳转与查询串变化，如 /proc?view=req ↔ ?view=ord）。
   指针式轨迹：新导航截断后悔分支；返回只移指针——watcher 见「到达轨迹当前位」
   自动跳过记录，无需标志位，不会因重复导航或快速连点而错位 */
const trail = ref<string[]>([])
const idx = ref(-1)
watch(() => route.fullPath, to => {
  if (trail.value[idx.value] === to) return   /* 返回到达的目标，或重复激活当前页 */
  trail.value = trail.value.slice(0, idx.value + 1)
  trail.value.push(to)
  if (trail.value.length > 50) trail.value.shift()
  idx.value = trail.value.length - 1
}, { immediate: true })
/* 系统首页固定为聊天。 */
const homePath = ref(defaultHome())
const isHome = computed(() => route.path === homePath.value)
function refreshHome() { homePath.value = defaultHome() }
watch(() => route.fullPath, refreshHome)
const canBack = computed(() => idx.value > 0)

/* 页签很多时：12 签上限内可能超出可视宽度（滚动条隐藏）——
   ① 垂直滚轮转横向滚动 ② 激活签变化时自动滚入视野 ③ 两端箭头 + 渐隐提示：
   有溢出才出现，点击按屏滚动，让「滚到之前的页签」有可见入口 */
const strip = ref<HTMLElement | null>(null)
const canL = ref(false)
const canR = ref(false)
function updArrows() {
  const el = strip.value
  if (!el) return
  canL.value = el.scrollLeft > 1
  canR.value = el.scrollLeft < el.scrollWidth - el.clientWidth - 1
}
function scrollStrip(dir: number) {
  const el = strip.value
  if (!el) return
  el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: 'smooth' })
}
function onWheel(e: WheelEvent) {
  const el = strip.value
  if (!el || el.scrollWidth <= el.clientWidth) return
  if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
    el.scrollLeft += e.deltaY
    e.preventDefault()
  }
}
watch(() => wt.activeId, () => {
  void nextTick(() => {
    strip.value?.querySelector('.wt-tab.on')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  })
})
/* 页签增减 / 窗口尺寸变化后刷新箭头可见性 */
watch(() => wt.tabs.length, () => void nextTick(updArrows), { immediate: true })
function onWinResize() { updArrows() }

function activate(path: string) {
  if (route.fullPath !== path) router.push(path)
}
function close(id: string) {
  const next = closeTab(id)
  if (next && route.fullPath !== next) router.push(next)
}
/* 关闭其他：保留当前激活标签与固定标签（聊天首页签），其余标签不分左右全部关闭；没有可关闭标签时按钮禁用 */
const canCloseOthers = computed(() =>
  wt.tabs.some(t => !t.pinned && t.id !== wt.activeId)
)
function closeOtherTabs() {
  const n = closeOthers()
  if (n > 0) toast('已关闭其他 ' + n + ' 个标签页')
}
function goBack() {
  if (idx.value <= 0) return
  idx.value--
  router.push(trail.value[idx.value])
}
function goHome() {
  refreshHome()
  if (isHome.value) return
  router.push(homePath.value)
}
/* 品牌区：点击访问产品主页（新窗口）；悬停浮窗展示平台与各模块版本，
   点击版本号打开「版本更新」抽屉查看该模块的更新内容。
   版本号唯一权威 = 版本管理台账（/api/versions/ledger，platform.app_release* 派生），
   与「平台管理 → 版本管理」页一致；未登记的模块如实显示「未登记」。 */
const SITE_URL = 'https://ema.chat'
function openSite() {
  window.open(SITE_URL, '_blank', 'noopener')
}
/* 台账：moduleCode（npm 包名，如 ema-web / ema-server / @ema/module-tasks）→ 登记项。
   null = 未加载/加载中；'fail' = 获取失败 */
const ledger = ref<Map<string, VersionLedgerItem> | 'fail' | null>(null)
let ledgerFetching = false
async function loadLedger() {
  if (ledger.value !== null || ledgerFetching) return
  ledgerFetching = true
  try {
    const rows = await versionPublicRepo.ledger()
    ledger.value = new Map(rows.map(r => [r.moduleCode, r]))
  } catch {
    ledger.value = 'fail'
  } finally {
    ledgerFetching = false
  }
}
function retryLedger() { ledger.value = null; void loadLedger() }
/* 某包已登记版本；'' = 加载中 / 未登记 / 获取失败（由调用处区分展示） */
function ledgerVer(pkg: string): string {
  if (ledger.value === null || ledger.value === 'fail') return ''
  return ledger.value.get(pkg)?.version ?? ''
}
/* 浮窗模块行：按启用模块出列，版本取台账中对应 npm 包（@ema/module-<code>） */
const moduleRows = computed(() => ENABLED_MODULES
  .map(m => ({ code: m.code, title: m.title, pkg: `@ema/module-${m.code}`, version: ledgerVer(`@ema/module-${m.code}`) })))
/* 浮窗固定：悬停只临时显示；点图钉后常驻，再点取消（不占用品牌单击=访问主页） */
const popPinned = ref(false)
function togglePin() { popPinned.value = !popPinned.value }
/* 「版本更新」抽屉：按包名拉取版本管理登记的更新日志（最新在前） */
const updOpen = ref(false)
const updTitle = ref('')
const updPkg = ref('')
const updReleases = ref<VersionRelease[]>([])
const updLoading = ref(false)
const updErr = ref('')
async function showUpdates(pkg: string, title: string) {
  updPkg.value = pkg
  updTitle.value = title
  updReleases.value = []
  updErr.value = ''
  updOpen.value = true
  updLoading.value = true
  try {
    updReleases.value = await versionPublicRepo.releases(pkg)
  } catch (e) {
    updErr.value = (e as Error).message
  } finally {
    updLoading.value = false
  }
}
/* 本次登记中该包获得的版本号 */
function relVersion(r: VersionRelease): string {
  return r.modules.find(m => m.moduleCode === updPkg.value)?.version ?? ''
}
function fmtDay(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function onKey(e: KeyboardEvent) {
  const tag = (e.target as HTMLElement)?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA') return
  if (e.key === 'Escape' && canBack.value) { e.preventDefault(); goBack() }
  else if (e.altKey && e.key === 'ArrowLeft') { e.preventDefault(); goBack() }
  else if (e.altKey && (e.key === 'h' || e.key === 'H')) { e.preventDefault(); goHome() }
}
onMounted(() => {
  document.addEventListener('keydown', onKey)
  window.addEventListener('resize', onWinResize)
  on('defaults:changed', refreshHome)
  void nextTick(updArrows)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', onWinResize)
  off('defaults:changed', refreshHome)
})
</script>

<template>
  <div class="workbar">
    <!-- 品牌：常驻可见；点击访问产品主页，悬停看平台与各模块版本（版本管理台账），
         点版本号打开「版本更新」抽屉 -->
    <button type="button" class="wb-brand"
            title="企管助手（ema.chat） · 点击访问产品主页"
            @click="openSite" @mouseenter="loadLedger">
      <span class="wb-mark">企</span>
      <span class="wb-bn">企管助手（ema.chat）</span>
      <span class="wb-pop" :class="{ pin: popPinned }" @click.stop>
        <i class="wb-pin" :class="{ on: popPinned }" role="button"
           :title="popPinned ? '取消固定' : '固定浮窗（鼠标移开不消失）'" @click.stop="togglePin">
          <svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor">
            <path d="M5.5 1.5h5l.8 4.6 2.2 2.9H2.5l2.2-2.9zM7 10h2l-1 4.5z" />
          </svg>
        </i>
        <div class="wb-plat">
          <b>平台</b>
          <template v-if="ledger === 'fail'">
            <span class="wb-ver off">版本获取失败</span>
            <span class="wb-ver" role="button" title="重新加载版本台账" @click.stop="retryLedger">重试</span>
          </template>
          <span v-else-if="ledger === null" class="wb-ver off">…</span>
          <template v-else>
            <span v-if="ledgerVer('ema-web')" class="wb-ver" role="button" title="平台前端更新内容"
                  @click.stop="showUpdates('ema-web', '平台前端')">前端 v{{ ledgerVer('ema-web') }}</span>
            <span v-else class="wb-ver off" title="尚未在版本管理中登记">前端 未登记</span>
            <span v-if="ledgerVer('ema-server')" class="wb-ver" role="button" title="平台后端更新内容"
                  @click.stop="showUpdates('ema-server', '平台后端')">后端 v{{ ledgerVer('ema-server') }}</span>
            <span v-else class="wb-ver off" title="尚未在版本管理中登记">后端 未登记</span>
          </template>
        </div>
        <div class="wb-mods">
          <div v-for="r in moduleRows" :key="r.code" class="wb-mod">
            <span class="wb-mod-n">{{ r.title }}</span>
            <span v-if="ledger === null" class="wb-ver off">…</span>
            <span v-else-if="ledger === 'fail'" class="wb-ver off">—</span>
            <span v-else-if="r.version" class="wb-ver" role="button" :title="r.title + ' 更新内容'"
                  @click.stop="showUpdates(r.pkg, r.title)">v{{ r.version }}</span>
            <span v-else class="wb-ver off" title="尚未在版本管理中登记">未登记</span>
          </div>
        </div>
        <span class="wb-pop-s">版本号以「平台管理 → 版本管理」登记为准，点击查看更新内容</span>
      </span>
    </button>
    <i class="wb-sep" />

    <!-- 版本更新抽屉：版本管理登记的更新日志（最新在前） -->
    <UDrawer :open="updOpen" :title="updTitle + ' · 版本更新'" width="420px" @close="updOpen = false">
      <p v-if="updLoading" class="wu-empty">正在读取更新日志…</p>
      <p v-else-if="updErr" class="wu-empty">
        加载失败：{{ updErr }}
        <span class="wb-ver" role="button" style="margin-left:6px"
              @click="showUpdates(updPkg, updTitle)">重试</span>
      </p>
      <p v-else-if="!updReleases.length" class="wu-empty">暂无更新记录</p>
      <section v-for="e in updReleases" :key="e.id" class="wu-sec">
        <header class="wu-h"><b>v{{ relVersion(e) || e.versionNo }}</b><span class="wu-d">{{ e.versionNo }} · {{ fmtDay(e.createdAt) }}</span></header>
        <ul class="wu-l">
          <li>{{ e.title }}</li>
        </ul>
        <p v-if="e.detail" class="wu-dt">{{ e.detail }}</p>
      </section>
    </UDrawer>

    <!-- 导航四按钮：AI 助手 / 主页 / 批量关闭 / 返回 -->
    <div class="wb-nav">
      <button class="wb-btn ab-on" :class="{ on: buddy.open.value }" title="AI 助手（悬浮 · 可拖拽）" @click="buddy.toggle()">
        <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor">
          <path d="M8 1.6 9.5 6l4.5 1.5L9.5 9 8 13.5 6.5 9 2 7.5 6.5 6z" />
          <circle cx="13" cy="13" r="1.3" />
        </svg>
      </button>
      <button class="wb-btn" :disabled="isHome"
              :title="isHome ? '已在默认主页' : '回到默认主页'" @click="goHome">
        <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor"
             stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2.5 7.5 8 3l5.5 4.5" />
          <path d="M4.5 6.8V13h7V6.8" />
        </svg>
      </button>
      <button class="wb-btn" :disabled="!canCloseOthers"
              :title="canCloseOthers ? '关闭其他标签页（保留当前页与固定的聊天签）' : '没有可关闭的其他标签页'"
              @click="closeOtherTabs">
        <!-- 关闭其他：纯 ×（不带窗口框；保留激活签与固定签，其余页签不分左右全部关闭） -->
        <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3.8 3.8l8.4 8.4M12.2 3.8l-8.4 8.4" />
        </svg>
      </button>
      <button class="wb-btn" :disabled="!canBack"
              :title="canBack ? '返回上一次操作' : '没有可返回的页面'" @click="goBack">
        <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor"
             stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <path d="M13 8H3.5M7 4.5 3.5 8 7 11.5" />
        </svg>
      </button>
    </div>

    <!-- 分割线②：导航区 | 工作页签列（与品牌区分割线同款，工作条自此分为 品牌 | 导航 | 页签 三段） -->
    <i class="wb-sep" />

    <!-- 工作页签列（溢出时两端出现箭头与渐隐，点击箭头按屏滚动） -->
    <div class="wt-wrap">
      <transition name="wt-fade">
        <i v-if="canL" class="wt-shade l" />
      </transition>
      <transition name="wt-fade">
        <button v-if="canL" class="wt-arr l" title="向左滚动页签" @click="scrollStrip(-1)">‹</button>
      </transition>
      <div ref="strip" class="wt-strip" @wheel="onWheel" @scroll.passive="updArrows">
        <button v-for="tab in wt.tabs" :key="tab.id" class="wt-tab"
                :class="{ on: tab.id === wt.activeId }"
                :title="tab.title" @click="activate(tab.path)">
          <span class="wt-nm">{{ tab.title }}</span>
          <i v-if="!tab.pinned" class="wt-x" title="关闭标签" @click.stop="close(tab.id)">×</i>
        </button>
      </div>
      <transition name="wt-fade">
        <button v-if="canR" class="wt-arr r" title="向右滚动页签" @click="scrollStrip(1)">›</button>
      </transition>
      <transition name="wt-fade">
        <i v-if="canR" class="wt-shade r" />
      </transition>
    </div>
  </div>
</template>

<style scoped>
.workbar {
  height: var(--workbar-height); flex: none;
  display: flex; align-items: center; gap: 6px; padding: 0 12px;
  background: var(--surface); border-bottom: 1px solid var(--line);
  position: relative; z-index: 150; min-width: 0;
}
/* 导航五按钮：与品牌区同线居中 */
.wb-nav { display: flex; align-items: center; gap: 6px; flex: none; }
.wb-btn {
  width: 26px; height: 26px; border-radius: 7px; flex: none;
  display: grid; place-items: center;
  border: 1px solid var(--line); background: var(--surface-3); color: var(--ink-2);
}
.wb-btn:hover:not(:disabled) { background: var(--accent-soft); border-color: var(--accent-line); color: var(--accent); }
.wb-btn:disabled { opacity: .38; cursor: default; }
.wb-btn.on { background: linear-gradient(135deg, var(--accent), var(--violet)); border-color: transparent; color: #fff; box-shadow: var(--sh-1); }
.wb-brand { display: flex; align-items: center; gap: 7px; flex: none; padding: 3px 6px 3px 2px; border-radius: 8px; position: relative; text-decoration: none; border: 0; background: transparent; cursor: pointer; font: inherit; }
.wb-brand:hover { background: var(--hover); }
/* 版本悬停浮窗：平台 + 各模块前后端版本，点版本号开「版本更新」抽屉。
   ::before 桥接按钮与浮窗间的 6px 空隙，鼠标下移不丢 hover，浮窗不闪退 */
.wb-pop {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 60;
  min-width: 300px; padding: 10px 12px;
  background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-m);
  box-shadow: var(--sh-3); text-align: left; cursor: default;
  opacity: 0; visibility: hidden; transform: translateY(-4px);
  transition: opacity .18s ease .25s, transform .18s ease .25s, visibility 0s linear .43s;
  pointer-events: none;
}
.wb-pop::before { content: ''; position: absolute; left: 0; right: 0; top: -6px; height: 6px; }
.wb-brand:hover .wb-pop,
.wb-pop.pin { opacity: 1; visibility: visible; transform: translateY(0); transition-delay: .25s, .25s, 0s; pointer-events: auto; }
/* 固定后切换不再延迟，立即显隐 */
.wb-pop.pin { transition-delay: 0s, 0s, 0s; }
/* 图钉：固定浮窗开关，置顶不受模块列表滚动影响 */
.wb-pin {
  position: absolute; top: 7px; right: 8px; z-index: 2;
  width: 18px; height: 18px; border-radius: 5px;
  display: grid; place-items: center; color: var(--ink-4); cursor: pointer;
}
.wb-pin:hover { background: var(--hover); color: var(--ink-2); }
.wb-pin.on { color: var(--accent); background: var(--accent-soft); }
.wb-plat { display: flex; align-items: center; gap: 6px; padding-bottom: 6px; border-bottom: 1px solid var(--line-soft); }
.wb-plat b { font-size: var(--fs-sm); color: var(--ink); margin-right: 2px; }
/* 模块行紧凑排列，超出高度浮窗内滚动 */
.wb-mods { margin-top: 4px; max-height: 46vh; overflow-y: auto; }
.wb-mod { display: flex; align-items: center; gap: 6px; padding: 3px 0; }
.wb-mod-n { flex: 1; min-width: 0; font-size: var(--fs-xs); color: var(--ink-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* 版本号：可点，悬停示意进入更新内容 */
.wb-ver {
  flex: none; padding: 1px 6px; border-radius: 5px;
  font-size: var(--fs-xs); color: var(--accent); background: var(--accent-soft);
  cursor: pointer; white-space: nowrap;
}
.wb-ver:hover { outline: 1px solid var(--accent-line); }
/* 不可点状态：加载中 / 获取失败 / 后端未启用 */
.wb-ver.off { color: var(--ink-4); background: var(--surface-3); cursor: default; }
.wb-ver.off:hover { outline: none; }
.wb-pop-s { display: block; margin-top: 6px; font-size: var(--fs-xs); font-weight: var(--fw-normal); color: var(--ink-4); }
.wb-mark {
  width: 24px; height: 24px; border-radius: 7px;
  background: linear-gradient(140deg, var(--accent), var(--violet));
  display: grid; place-items: center; color: #fff;
  font-size: 12px; font-weight: var(--fw-bold); box-shadow: var(--sh-1);
}
.wb-bn { font-size: var(--fs-md); font-weight: var(--fw-semibold); color: var(--ink); white-space: nowrap; }
.wb-sep { width: 1px; height: 20px; background: var(--line-soft); flex: none; margin: 0 2px; }

/* 页签列：与品牌/按钮同一垂直中线，不足撑满时靠左，超出横滚（滚动条隐藏，箭头代替） */
.wt-wrap { position: relative; flex: 1; min-width: 0; height: 100%; display: flex; align-items: center; }
.wt-strip { display: flex; align-items: center; gap: 3px; width: 100%; min-width: 0; overflow-x: auto; scrollbar-width: none; height: 100%; }
.wt-strip::-webkit-scrollbar { display: none; }
/* 溢出提示：边缘渐隐 + 悬浮箭头（有溢出方向才出现） */
.wt-shade {
  position: absolute; top: 0; bottom: 0; width: 26px; z-index: 2; pointer-events: none;
}
.wt-shade.l { left: 0; background: linear-gradient(90deg, var(--surface) 20%, transparent); }
.wt-shade.r { right: 0; background: linear-gradient(270deg, var(--surface) 20%, transparent); }
.wt-arr {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 3;
  width: 18px; height: 18px; border-radius: 50%;
  display: grid; place-items: center;
  border: 1px solid var(--line); background: var(--surface); color: var(--ink-2);
  font-size: 13px; line-height: 1; box-shadow: var(--sh-1);
}
.wt-arr:hover { background: var(--accent-soft); border-color: var(--accent-line); color: var(--accent); }
.wt-arr.l { left: -2px; }
.wt-arr.r { right: -2px; }
.wt-fade-enter-active, .wt-fade-leave-active { transition: opacity .18s; }
.wt-fade-enter-from, .wt-fade-leave-to { opacity: 0; }
.wt-tab {
  display: flex; align-items: center; gap: 7px; flex: none;
  max-width: 190px; height: 30px; padding: 0 10px;
  border: 1px solid var(--line-soft); border-radius: 8px;
  background: var(--bg);
  font-size: var(--fs-xs); color: var(--ink-3);
}
.wt-tab:hover { background: var(--hover); border-color: var(--line); color: var(--ink-2); }
.wt-tab.on {
  background: var(--accent-soft); border-color: var(--accent-line);
  color: var(--accent); font-weight: var(--fw-medium);
}
.wt-nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wt-x {
  font-style: normal; flex: none; width: 16px; height: 16px; border-radius: 4px;
  display: grid; place-items: center; font-size: 13px; line-height: 1; color: var(--ink-4);
}
.wt-x:hover { background: var(--line); color: var(--ink); }

/* 「版本更新」抽屉内容 */
.wu-empty { margin: 4px 0; font-size: var(--fs-sm); color: var(--ink-4); }
.wu-sec + .wu-sec { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--line-soft); }
.wu-h { display: flex; align-items: baseline; gap: 8px; }
.wu-h b { font-size: var(--fs-md); color: var(--ink); }
.wu-d { font-size: var(--fs-xs); color: var(--ink-4); }
.wu-l { margin: 6px 0 0; padding-left: 18px; }
.wu-l li { font-size: var(--fs-sm); color: var(--ink-2); line-height: 1.7; }
.wu-dt { margin: 4px 0 0; font-size: var(--fs-xs); color: var(--ink-3); white-space: pre-line; }

@media (max-width: 760px) {
  .workbar { padding: 0 6px; gap: 3px; }
  .wt-nm { max-width: 88px; }
  .wb-bn, .wb-sep { display: none; }
}
</style>
